import { useState } from 'react';
import { MessageRow } from '../../../types/supabase';
import { MessageSummary } from './MessageSummary';
import { MessageBody } from './MessageBody';
import { useSession } from '../../providers/supabase';
import './MessageView.scss';

interface MessageViewProps {
  message: MessageRow;
  isExpanded?: boolean;
  onToggle?: () => void;
  showToggle?: boolean;
}

export function MessageView({ 
  message, 
  isExpanded = false,
  onToggle,
  showToggle = true
}: MessageViewProps) {
  const [internalExpanded, setInternalExpanded] = useState(isExpanded);
  const session = useSession();
  
  const expanded = onToggle !== undefined ? isExpanded : internalExpanded;
  const handleToggle = onToggle || (() => setInternalExpanded(!internalExpanded));

  const getMessageColorClass = (message: MessageRow) => {
    const senderType = getSenderType();
    switch (senderType) {
      case 'user':
        return 'message-user';
      case 'alice':
        return 'message-system';
      case 'system':
        return 'message-system';
      case 'provider':
        return 'message-provider';
      default:
        return 'message-system';
    }
  };

  const getMessageIcon = (message: MessageRow) => {
    // For now, email is the only communication method
    return 'mail';
  };

  const getSenderIcon = () => {
    if (message.direction === 'INBOUND') {
      return 'arrow_downward';
    } else if (message.direction === 'OUTBOUND') {
      return 'arrow_outward';
    } else {
      return 'question_mark';
    }
  };


  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const getSenderType = () => {
    // For provider conversations:
    // - OUTBOUND messages are from Alice (the AI assistant) to the provider
    // - INBOUND messages are from the provider or the member
    if (message.direction === 'OUTBOUND') {
      return 'alice';
    } else if (message.direction === 'INBOUND') {
      // In-app replies are from the user, not the provider
      if (message.channel === 'in-app') {
        return 'user';
      }
      // Messages coming in from external sources
      const senderEmail = message.from_address;
      if (senderEmail?.includes('healthsharetech.com') || senderEmail?.includes('@localhost')) {
        return 'system';
      } else {
        return 'provider';  // Blue/purple color - represents the provider responding
      }
    } else {
      return 'system';
    }
  };

  const getSenderDisplay = () => {
    const senderType = getSenderType();
    if (senderType === 'user') {
      return 'You';
    } else if (senderType === 'alice') {
      return 'Alice';
    } else if (senderType === 'system') {
      return 'System';
    } else {
      return 'Provider';
    }
  };

  if (!expanded && showToggle && onToggle) {
    return (
      <div className={`message-card collapsed ${getMessageColorClass(message)}`}>
        <MessageSummary 
          message={message}
          showDirection={true}
          onClick={handleToggle}
          className="clickable"
        />
      </div>
    );
  }

  return (
    <div className={`message-card expanded ${getMessageColorClass(message)}`}>
      <div className="message-header">
        <div className="message-icon">
          <i className="material-icons">{getMessageIcon(message)}</i>
          <i className="material-icons">{getSenderIcon()}</i>
        </div>
        <div className="message-meta">
          <div className="message-direction">
            {getSenderDisplay()}
          </div>
          <div className="message-time">
            {formatMessageTime(message.created_at)}
          </div>
        </div>
        {message.status && (
          <div className="message-status">
            <span className={`badge ${message.status === 'delivered' ? 'bg-success' : 
                                     message.status === 'pending' ? 'bg-warning' : 
                                     message.status === 'failed' ? 'bg-danger' : 'bg-secondary'}`}>
              {message.status}
            </span>
          </div>
        )}
        {showToggle && onToggle && (
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={handleToggle}
            title="Collapse message"
          >
            <i className="material-icons">expand_less</i>
          </button>
        )}
      </div>
      
      {message.subject && (
        <div className="message-subject">
          <strong>{message.subject}</strong>
        </div>
      )}
      
      {message.body_text && (
        <div className="message-body">
          <MessageBody text={message.body_text} className="message-content" />
        </div>
      )}
    </div>
  );
}